import React, { useState, useContext } from 'react';
import {
  Box,
  Text,
  VStack,
  Flex,
  IconButton,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  HStack,
  useToast,
  Spinner,
} from '@chakra-ui/react';
import { DeleteIcon, AddIcon, DragHandleIcon } from '@chakra-ui/icons';
import { FaInstagram, FaTiktok } from 'react-icons/fa';
import { Bundle, RateRanges } from '@foodfluence/shared';
import { getFormattedBundleDescription } from '../utils';
import { influencerApi } from 'services/api/influencer';
import { InfluencerDataContext } from 'contexts/InfluencerDataContext';
import AddBundleModal from './EditProfileComponents/AddBundleModal';
import { ff } from '../../../theme';

interface RatesEditProps {
  rateRanges: RateRanges;
  setRateRanges: (ranges: RateRanges) => void;
  bundlePricing: Bundle[];
  setBundlePricing: (bundles: Bundle[]) => void;
  collabPreference: string;
  lastSavedRatesInfo: {
    rateRanges: RateRanges;
    bundlePricing: Bundle[];
  } | null;
  onRatesSaved: (rateRanges: RateRanges, bundlePricing: Bundle[]) => void;
  onBack: () => void;
}

const RatesEdit: React.FC<RatesEditProps> = ({
  rateRanges,
  setRateRanges,
  bundlePricing,
  setBundlePricing,
  collabPreference,
  lastSavedRatesInfo,
  onRatesSaved,
  onBack,
}) => {
  const toast = useToast();
  const { influencerData } = useContext(InfluencerDataContext);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSavingRates, setIsSavingRates] = useState(false);
  const [savingRateField, setSavingRateField] = useState<string | null>(null);
  const [isAddBundleModalOpen, setIsAddBundleModalOpen] = useState(false);
  const cashCap = 10000;

  /**
   * Autosave rates to backend
   */
  const autosaveRates = async (updatedRateRanges: RateRanges, fieldKey: string) => {
    if (!lastSavedRatesInfo || !influencerData?.id) return;
    
    try {
      setIsSavingRates(true);
      setSavingRateField(fieldKey);
      
      await influencerApi.updateProfile({
        rateRanges: updatedRateRanges,
        bundlePricing
      }, influencerData.id);
      
      // Update last saved value on success
      onRatesSaved(updatedRateRanges, bundlePricing);
    } catch (error) {
      console.error('Error autosaving rates:', error);
      // Revert to last saved value
      setRateRanges(lastSavedRatesInfo.rateRanges);
      toast({
        title: "Couldn't save rate",
        description: "Reverted to last saved value",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSavingRates(false);
      setSavingRateField(null);
    }
  };
  
  /**
   * Autosave bundles to backend
   */
  const autosaveBundles = async (updatedBundles: Bundle[]) => {
    if (!lastSavedRatesInfo || !influencerData?.id) return;
    
    try {
      setIsSavingRates(true);
      
      await influencerApi.updateProfile({
        bundlePricing: updatedBundles,
        rateRanges
      }, influencerData.id);
      
      // Update last saved value on success
      onRatesSaved(rateRanges, updatedBundles);
    } catch (error) {
      console.error('Error autosaving bundles:', error);
      // Revert to last saved value
      setBundlePricing(lastSavedRatesInfo.bundlePricing);
      toast({
        title: "Couldn't save bundles",
        description: "Reverted to last saved",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSavingRates(false);
    }
  };

  const handleNewBundle = async (newBundle: Bundle) => {
    const updatedBundles = [...bundlePricing, newBundle];
    setBundlePricing(updatedBundles);
    await autosaveBundles(updatedBundles);
  };

  const handleDeleteBundle = async (index: number) => {
    const updatedBundles = bundlePricing.filter((_, i) => i !== index);
    setBundlePricing(updatedBundles);
    await autosaveBundles(updatedBundles);
  };

  const handleRateChange = (category: string, field: string, value: string) => {
    const numValue = value === '' ? 0 : Number(value);
    
    const updatedRateRanges = {
      ...rateRanges,
      [category]: {
        ...rateRanges[category],
        [field]: numValue,
      },
    };
    
    setRateRanges(updatedRateRanges);
    
    if (numValue > cashCap) {
      setErrorMessage(`Rate cannot exceed $${cashCap.toLocaleString()}.`);
    } else {
      setErrorMessage(null);
    }
  };
  
  const handleRateBlur = (category: string, field: string) => {
    if (lastSavedRatesInfo) {
      const lastSavedValue = lastSavedRatesInfo.rateRanges[category]?.[field];
      const currentValue = rateRanges[category]?.[field];
      if (lastSavedValue !== currentValue && !errorMessage) {
        autosaveRates(rateRanges, `${category}.${field}`);
      }
    }
  };

  const isInstagramActive = rateRanges.instagramPosts?.offered && 
                           rateRanges.instagramReels?.offered && 
                           rateRanges.instagramStories?.offered;

  const isTikTokActive = rateRanges.tiktoks?.offered;

  return (
    <Box>
      <VStack spacing={6} align="stretch" mb={6}>
        {/* Instagram Rates */}
        {isInstagramActive && (
          <Box
            bg="white"
            borderRadius="20px"
            p={5}
            border="1px solid"
            borderColor={ff.line}
          >
            <Flex align="center" mb={5}>
              <Box 
                w="40px" 
                h="40px" 
                bg={ff.lime} 
                color={ff.ink}
                borderRadius="full" 
                display="flex" 
                alignItems="center" 
                justifyContent="center"
                mr={3}
              >
                <FaInstagram size={20} />
              </Box>
              <Text textStyle="display" fontSize="20px" color={ff.ink}>
                Instagram rates
              </Text>
              {isSavingRates && savingRateField?.startsWith('instagram') && (
                <Spinner size="sm" color={ff.muted} ml={2} />
              )}
            </Flex>

            <VStack spacing={4} align="stretch">
              {/* Reel */}
              <Box>
                <Text fontSize="14px" fontWeight={600} mb="6px" color={ff.ink}>
                  Reel
                </Text>
                <InputGroup>
                  <InputLeftElement pointerEvents="none" color={ff.text2} h="48px">
                    $
                  </InputLeftElement>
                  <Input
                    placeholder="0"
                    value={rateRanges.instagramReels?.minRate || ''}
                    onChange={(e) => handleRateChange('instagramReels', 'minRate', e.target.value)}
                    onBlur={() => handleRateBlur('instagramReels', 'minRate')}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleRateBlur('instagramReels', 'minRate');
                      }
                    }}
                    type="number"
                    pr={isSavingRates && savingRateField === 'instagramReels.minRate' ? '3rem' : undefined}
                  />
                  {isSavingRates && savingRateField === 'instagramReels.minRate' && (
                    <InputRightElement width="3rem">
                      <Spinner size="sm" color={ff.muted} />
                    </InputRightElement>
                  )}
                </InputGroup>
              </Box>

              {/* Post */}
              <Box>
                <Text fontSize="14px" fontWeight={600} mb="6px" color={ff.ink}>
                  Post
                </Text>
                <InputGroup>
                  <InputLeftElement pointerEvents="none" color={ff.text2} h="48px">
                    $
                  </InputLeftElement>
                  <Input
                    placeholder="0"
                    value={rateRanges.instagramPosts?.minRate || ''}
                    onChange={(e) => handleRateChange('instagramPosts', 'minRate', e.target.value)}
                    onBlur={() => handleRateBlur('instagramPosts', 'minRate')}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleRateBlur('instagramPosts', 'minRate');
                      }
                    }}
                    type="number"
                    pr={isSavingRates && savingRateField === 'instagramPosts.minRate' ? '3rem' : undefined}
                  />
                  {isSavingRates && savingRateField === 'instagramPosts.minRate' && (
                    <InputRightElement width="3rem">
                      <Spinner size="sm" color={ff.muted} />
                    </InputRightElement>
                  )}
                </InputGroup>
              </Box>

              {/* Story */}
              <Box>
                <Text fontSize="14px" fontWeight={600} mb="6px" color={ff.ink}>
                  Story
                </Text>
                <InputGroup>
                  <InputLeftElement pointerEvents="none" color={ff.text2} h="48px">
                    $
                  </InputLeftElement>
                  <Input
                    placeholder="0"
                    value={rateRanges.instagramStories?.minRate || ''}
                    onChange={(e) => handleRateChange('instagramStories', 'minRate', e.target.value)}
                    onBlur={() => handleRateBlur('instagramStories', 'minRate')}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleRateBlur('instagramStories', 'minRate');
                      }
                    }}
                    type="number"
                    pr={isSavingRates && savingRateField === 'instagramStories.minRate' ? '3rem' : undefined}
                  />
                  {isSavingRates && savingRateField === 'instagramStories.minRate' && (
                    <InputRightElement width="3rem">
                      <Spinner size="sm" color={ff.muted} />
                    </InputRightElement>
                  )}
                </InputGroup>
              </Box>
            </VStack>
          </Box>
        )}

        {/* TikTok Rates */}
        {isTikTokActive && (
          <Box
            bg="white"
            borderRadius="20px"
            p={5}
            border="1px solid"
            borderColor={ff.line}
          >
            <Flex align="center" mb={5}>
              <Box 
                w="40px" 
                h="40px" 
                bg={ff.lime} 
                color={ff.ink}
                borderRadius="full" 
                display="flex" 
                alignItems="center" 
                justifyContent="center"
                mr={3}
              >
                <FaTiktok size={20} />
              </Box>
              <Text textStyle="display" fontSize="20px" color={ff.ink}>
                TikTok rates
              </Text>
              {isSavingRates && savingRateField?.startsWith('tiktoks') && (
                <Spinner size="sm" color={ff.muted} ml={2} />
              )}
            </Flex>

            <Box>
              <Text fontSize="14px" fontWeight={600} mb="6px" color={ff.ink}>
                Video
              </Text>
              <InputGroup>
                <InputLeftElement pointerEvents="none" color={ff.text2} h="48px">
                  $
                </InputLeftElement>
                <Input
                  placeholder="0"
                  value={rateRanges.tiktoks?.minRate || ''}
                  onChange={(e) => handleRateChange('tiktoks', 'minRate', e.target.value)}
                  onBlur={() => handleRateBlur('tiktoks', 'minRate')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleRateBlur('tiktoks', 'minRate');
                    }
                  }}
                  type="number"
                  pr={isSavingRates && savingRateField === 'tiktoks.minRate' ? '3rem' : undefined}
                />
                {isSavingRates && savingRateField === 'tiktoks.minRate' && (
                  <InputRightElement width="3rem">
                    <Spinner size="sm" color={ff.muted} />
                  </InputRightElement>
                )}
              </InputGroup>
            </Box>
          </Box>
        )}

        {/* Content Bundles */}
        <Box
          bg="white"
          borderRadius="20px"
          p={5}
          border="1px solid"
          borderColor={ff.line}
        >
          <Flex align="center" justify="space-between" mb={4}>
            <Flex align="center">
              <Box 
                w="40px" 
                h="40px" 
                bg={ff.lime} 
                color={ff.ink}
                borderRadius="full" 
                display="flex" 
                alignItems="center" 
                justifyContent="center"
                mr={3}
              >
                <Text fontSize="20px">📦</Text>
              </Box>
              <Text textStyle="display" fontSize="20px" color={ff.ink}>
                Content bundles
              </Text>
            </Flex>
            <Button
              variant="lime"
              h="44px"
              px={4}
              fontSize="14px"
              leftIcon={<AddIcon boxSize={3} />}
              onClick={() => setIsAddBundleModalOpen(true)}
              isDisabled={isSavingRates}
            >
              Create
            </Button>
          </Flex>

          {bundlePricing.length > 0 ? (
            <VStack spacing={3} align="stretch">
              {bundlePricing.map((bundle, index) => (
                <Box
                  key={index}
                  bg={ff.grey}
                  borderRadius="20px"
                  px={4}
                  py={3}
                >
                  <Flex align="center" justify="space-between">
                    <Box flex={1}>
                      <Flex align="center" mb={1}>
                        <Text fontWeight={600} fontSize="15px" color={ff.ink}>
                          {bundle.name ? bundle.name : 'Bundle'}
                        </Text>
                      </Flex>
                      <Text fontSize="13px" color={ff.text2}>
                        {getFormattedBundleDescription(bundle)}
                      </Text>
                    </Box>
                    <HStack spacing={0} mr={-2}>
                      <IconButton
                        icon={<DragHandleIcon />}
                        w="44px"
                        h="44px"
                        minW="44px"
                        borderRadius="full"
                        aria-label="Drag"
                        variant="ghost"
                        color={ff.muted}
                        cursor="grab"
                      />
                      <IconButton
                        icon={<DeleteIcon />}
                        w="44px"
                        h="44px"
                        minW="44px"
                        borderRadius="full"
                        aria-label="Delete Bundle"
                        variant="ghost"
                        color={ff.danger}
                        onClick={() => handleDeleteBundle(index)}
                        isDisabled={isSavingRates}
                      />
                    </HStack>
                  </Flex>
                </Box>
              ))}
            </VStack>
          ) : (
            <Text fontSize="14px" color={ff.text2}>
              No bundles created yet
            </Text>
          )}
        </Box>

        {errorMessage && (
          <Text color={ff.danger} fontSize="14px" textAlign="center">
            {errorMessage}
          </Text>
        )}
      </VStack>
      
      {/* Add Bundle Modal */}
      {isAddBundleModalOpen && (
        <AddBundleModal
          isOpen={isAddBundleModalOpen}
          onClose={() => setIsAddBundleModalOpen(false)}
          onBundleCreate={handleNewBundle}
        />
      )}
    </Box>
  );
};

export default RatesEdit;